/**
 * Admin Projects Store
 * Handles fetching and managing project data for admin users.
 */
import { defineStore } from "pinia";
import { ref, computed } from 'vue';
import { collection, getDocs, query, doc, updateDoc, addDoc, where, serverTimestamp, getDoc, deleteDoc, writeBatch } from "firebase/firestore";
import { db } from "../js/firebase";
import { logger } from '../services/loggerService';
import { PROJECT_STATUS } from '../utils/projectUtils';
import { useFormBuilderStore } from './formBuilderStore';
import { useUserAuthStore } from './userAuthStore';
import { deliverableService } from '../services/deliverableService';
import { projectService } from '../services/projectService';
import { reviewService } from '../services/reviewService';
import { contractService } from '../services/contractService';
import { applyMsaOverrideToBriefSections } from '../services/pricingCalculatorService';
import { valuesEqual } from '@/utils/valueCompare';
import {
  batchDeleteSubcollections,
  deleteProjectSubcollections
} from '../services/firestoreUtils';

// Firebase collection reference
const projectCollectionRef = collection(db, "projects");

export const useAdminProjectsStore = defineStore('adminProjects', () => {
  // ─── External Stores ───────────────────────────────────────
  const userAuthStore = useUserAuthStore();
  const formBuilderStore = useFormBuilderStore();

  //-------------------------------
  // STATE
  //-------------------------------
  /**
   * Object containing all projects keyed by ID
   * @type {Object}
   */
  const items = ref({});
  
  /**
   * Loading state for async operations
   * @type {Boolean}
   */
  const loading = ref(false);
  
  /**
   * Whether the store has completed initial data load
   * @type {Boolean}
   */
  const initialized = ref(false);

  /**
   * Deliverables for current project
   * @type {Array}
   */
  const deliverables = ref([]);
  
  /**
   * Loading state for deliverables
   * @type {Boolean}
   */
  const deliverablesLoading = ref(false);
  
  /**
   * Current deliverable activity logs
   * @type {Array}
   */
  const currentDeliverableActivity = ref([]);
  
  /**
   * Unsubscribe function for deliverables listener
   * @type {Function|null}
   */
  let deliverablesUnsubscribe = null;

  //-------------------------------
  // ACTIONS
  //-------------------------------
  
  /**
   * Fetch all active projects from Firestore
   * @param {String} sortField - Field to sort results by
   */
  async function init(sortField = 'dateCreated') {
    if (initialized.value) {
      return;
    }

    try {
      logger.info('[AdminProjects] Starting project initialization');
      
      // Simpler query that doesn't require a composite index
      const q = query(
        projectCollectionRef, 
        where("status", "!=", PROJECT_STATUS.ARCHIVED)
      );
      
      const querySnapshot = await getDocs(q);
      const projectsData = {};
      let activeCount = 0;
      let totalCount = 0;

      // Filter out episodes and sort the results in memory
      const docs = querySnapshot.docs
        .map(doc => ({ id: doc.id, ...doc.data() }))
        .filter(doc => doc.type !== 'episode')
        .sort((a, b) => {
          try {
            // First sort by status - with null/undefined safety
            const aStatus = a.status || '';
            const bStatus = b.status || '';
            const statusCompare = aStatus.localeCompare(bStatus);
            if (statusCompare !== 0) return statusCompare;
            
            // Then by the specified sort field - with null/undefined safety
            const aValue = a[sortField];
            const bValue = b[sortField];
            
            // Handle null/undefined values
            if (aValue == null && bValue == null) return 0;
            if (aValue == null) return 1; // nulls last
            if (bValue == null) return -1; // nulls last
            
            // Safe comparison
            if (aValue < bValue) return -1;
            if (aValue > bValue) return 1;
            return 0;
          } catch (error) {
            logger.error('[AdminProjects] Error in sorting projects:', {
              error: error.message,
              sortField,
              projectA: a.id,
              projectB: b.id
            });
            return 0; // Neutral sort on error
          }
        });

      // Process the sorted results
      docs.forEach(project => {
        projectsData[project.id] = project;
        if (project.status === 'active') activeCount++;
        totalCount++;
      });

      items.value = projectsData;
      logger.info('[AdminProjects] Projects loaded', { 
        total: totalCount, 
        active: activeCount 
      });

    } catch (error) {
      logger.error('[AdminProjects] Failed to fetch projects:', {
        error: error.message,
        code: error.code
      });
      throw error;
    } finally {
      initialized.value = true;
      loading.value = false;
    }
  }
  
  /**
   * Permanently delete a project from Firestore
   * @param {String} id - Project ID to delete
   */
  async function deleteProject(id) {
    if (!id) {
      logger.error('[AdminProjects] Invalid project delete attempt - missing ID');
      throw new Error('Project ID is required');
    }

    const project = items.value[id];
    if (!project) {
      logger.error('[AdminProjects] Project not found for deletion', { projectId: id });
      throw new Error('Project not found');
    }

    try {
      loading.value = true;

      logger.info('[AdminProjects] Deleting project permanently', {
        projectId: id,
        projectName: project.name,
        currentStatus: project.status
      });

      // Delete the Firestore document permanently
      const projectRef = doc(projectCollectionRef, id);
      await deleteDoc(projectRef);

      // Remove from local state
      delete items.value[id];

      // Clear from cache if exists
      if (projectCache.has(id)) {
        projectCache.delete(id);
      }

      logger.info('[AdminProjects] Project deleted successfully', {
        projectId: id,
        projectName: project.name
      });

      return { success: true };
    } catch (error) {
      logger.error('[AdminProjects] Failed to delete project:', {
        projectId: id,
        error: error.message
      });
      throw error;
    } finally {
      loading.value = false;
    }
  }

  /**
   * Get counts of related data that would be deleted with a project
   * @param {String} projectId - Project ID to check
   * @returns {Object} - { deliverableCount, eventCount }
   */
  async function getProjectDeletionCounts(projectId) {
    if (!projectId) {
      throw new Error('Project ID is required');
    }

    try {
      // Count deliverables
      const deliverablesRef = collection(db, 'deliverables');
      const deliverablesQuery = query(deliverablesRef, where('projectId', '==', projectId));
      const deliverablesSnapshot = await getDocs(deliverablesQuery);
      const deliverableCount = deliverablesSnapshot.size;

      // Count calendar events, split by whether they have an established date
      const calendarRef = collection(db, 'calendar');
      const calendarQuery = query(calendarRef, where('projectId', '==', projectId));
      const calendarSnapshot = await getDocs(calendarQuery);

      let datedEventCount = 0;
      let datelessEventCount = 0;
      for (const eventDoc of calendarSnapshot.docs) {
        if (eventDoc.data().date) {
          datedEventCount++;
        } else {
          datelessEventCount++;
        }
      }

      logger.info('[AdminProjects] Got project deletion counts', {
        projectId,
        deliverableCount,
        datedEventCount,
        datelessEventCount
      });

      return { deliverableCount, datedEventCount, datelessEventCount };
    } catch (error) {
      logger.error('[AdminProjects] Failed to get project deletion counts:', {
        projectId,
        error: error.message
      });
      throw error;
    }
  }

  /**
   * Delete a project and all related data (deliverables and calendar events)
   * @param {String} projectId - Project ID to delete
   * @returns {Object} - { success, deletedDeliverables, deletedEvents }
   */
  async function deleteProjectWithCascade(projectId) {
    if (!projectId) {
      logger.error('[AdminProjects] Invalid cascade delete attempt - missing ID');
      throw new Error('Project ID is required');
    }

    // Fall back to a direct read when the project isn't in the local store.
    // init() filters out `type === 'episode'`, so episodes are never in items —
    // without this, deleting a show's episodes fails with "Project not found".
    let project = items.value[projectId];
    if (!project) {
      const snap = await getDoc(doc(projectCollectionRef, projectId));
      if (!snap.exists()) {
        logger.error('[AdminProjects] Project not found for cascade deletion', { projectId });
        throw new Error('Project not found');
      }
      project = { id: snap.id, ...snap.data() };
    }

    try {
      loading.value = true;

      logger.info('[AdminProjects] Starting cascade deletion of project', {
        projectId,
        projectName: project.name
      });

      // Get all deliverables for this project
      const deliverablesRef = collection(db, 'deliverables');
      const deliverablesQuery = query(deliverablesRef, where('projectId', '==', projectId));
      const deliverablesSnapshot = await getDocs(deliverablesQuery);

      // Collect IDs for subcollection deletion
      const deliverableIds = deliverablesSnapshot.docs.map(d => d.id);

      // Delete subcollections for deliverables (discussions, activityLogs)
      if (deliverableIds.length > 0) {
        logger.info('[AdminProjects] Deleting deliverable subcollections', {
          count: deliverableIds.length
        });
        await batchDeleteSubcollections('deliverables', deliverableIds, ['discussions', 'activityLogs']);
      }

      // Delete review data for all deliverables (storage files, subcollections, share links, review docs)
      if (deliverableIds.length > 0) {
        logger.info('[AdminProjects] Deleting review data for deliverables', {
          count: deliverableIds.length
        });
        await reviewService.deleteReviewsForDeliverables(deliverableIds);
      }

      // Split calendar events: preserve dated events, delete dateless ones
      const calendarRef = collection(db, 'calendar');
      const calendarQuery = query(calendarRef, where('projectId', '==', projectId));
      const calendarSnapshot = await getDocs(calendarQuery);

      const datedEvents = [];
      const datelessEvents = [];
      for (const eventDoc of calendarSnapshot.docs) {
        if (eventDoc.data().date) {
          datedEvents.push(eventDoc);
        } else {
          datelessEvents.push(eventDoc);
        }
      }

      // Denormalize project/company names onto dated events (preserve as historical records)
      if (datedEvents.length > 0) {
        const preserveBatch = writeBatch(db);
        let preserveOps = 0;
        for (const eventDoc of datedEvents) {
          preserveBatch.update(eventDoc.ref, {
            projectName: project.name || 'Unknown Project',
            companyName: project.companyName || 'Unknown Company'
          });
          preserveOps++;
          if (preserveOps >= 500) {
            await preserveBatch.commit();
            preserveOps = 0;
          }
        }
        if (preserveOps > 0) {
          await preserveBatch.commit();
        }
        logger.info('[AdminProjects] Denormalized names onto dated calendar events', {
          projectId,
          preservedCount: datedEvents.length
        });
      }

      // Delete dateless events and their subcollections
      if (datelessEvents.length > 0) {
        const datelessIds = datelessEvents.map(d => d.id);
        await batchDeleteSubcollections('calendar', datelessIds, ['activity', 'productionSchedules']);

        const deleteBatch = writeBatch(db);
        let deleteOps = 0;
        for (const eventDoc of datelessEvents) {
          deleteBatch.delete(eventDoc.ref);
          deleteOps++;
          if (deleteOps >= 500) {
            await deleteBatch.commit();
            deleteOps = 0;
          }
        }
        if (deleteOps > 0) {
          await deleteBatch.commit();
        }
        logger.info('[AdminProjects] Deleted dateless calendar events', {
          projectId,
          deletedCount: datelessEvents.length
        });
      }

      // Delete project subcollections (activityLogs, productionLogs)
      await deleteProjectSubcollections(projectId);

      // Use batched writes for efficiency (Firestore limit is 500 operations per batch)
      const batch = writeBatch(db);
      let operationCount = 0;
      const MAX_BATCH_SIZE = 500;

      // Delete deliverables
      let deletedDeliverables = 0;
      for (const deliverableDoc of deliverablesSnapshot.docs) {
        batch.delete(deliverableDoc.ref);
        deletedDeliverables++;
        operationCount++;

        // If we hit batch limit, commit and create new batch
        if (operationCount >= MAX_BATCH_SIZE - 1) {
          await batch.commit();
          operationCount = 0;
        }
      }

      // Delete the project itself
      const projectRef = doc(projectCollectionRef, projectId);
      batch.delete(projectRef);

      // Commit any remaining operations
      await batch.commit();

      // Remove from local state
      delete items.value[projectId];

      // Clear from cache if exists
      if (projectCache.has(projectId)) {
        projectCache.delete(projectId);
      }

      logger.info('[AdminProjects] Project and related data deleted successfully', {
        projectId,
        projectName: project.name,
        deletedDeliverables,
        preservedEvents: datedEvents.length,
        deletedEvents: datelessEvents.length
      });

      return {
        success: true,
        deletedDeliverables,
        preservedEvents: datedEvents.length,
        deletedEvents: datelessEvents.length
      };
    } catch (error) {
      logger.error('[AdminProjects] Failed to cascade delete project:', {
        projectId,
        error: error.message
      });
      throw error;
    } finally {
      loading.value = false;
    }
  }

  /**
   * Update an existing project in Firestore and local state
   * @param {String} id - Project ID to update
   * @param {Object} projectData - Updated project data
   */
  async function updateProject(id, projectData) {
    if (!id || !projectData) {
      logger.error('[AdminProjects] Invalid project update attempt - missing data');
      throw new Error('Invalid project data or ID provided');
    }

    try {
      // Remove any non-serializable properties or UI-specific properties
      const cleanedData = { ...projectData };
      delete cleanedData.id; // ID should not be part of the document data

      // Update timestamp with Firebase serverTimestamp
      cleanedData.dateUpdated = serverTimestamp();
      
      // Function to recursively remove undefined values
      const removeUndefined = (obj) => {
        if (obj === null) {
          return null;
        }
        
        if (obj === undefined) {
          return undefined; // This will be filtered out at parent level
        }
        
        if (Array.isArray(obj)) {
          const cleanedArray = obj
            .map(item => removeUndefined(item))
            .filter(item => item !== undefined);
          return cleanedArray.length > 0 ? cleanedArray : [];
        }
        
        if (typeof obj === 'object' && obj.constructor === Object) {
          const cleaned = {};
          for (const [key, value] of Object.entries(obj)) {
            if (value !== undefined) {
              const cleanedValue = removeUndefined(value);
              if (cleanedValue !== undefined) {
                cleaned[key] = cleanedValue;
              }
            }
          }
          return Object.keys(cleaned).length > 0 ? cleaned : {};
        }
        
        // Handle other object types (Date, etc.) by returning as-is
        if (typeof obj === 'object') {
          return obj;
        }
        
        return obj;
      };
      
      // Clean the data to remove all undefined values
      const firebaseCleanedData = removeUndefined(cleanedData);
      
      logger.debug('[AdminProjects] Cleaned project data for Firestore:', {
        projectId: id,
        originalKeys: Object.keys(cleanedData).length,
        cleanedKeys: Object.keys(firebaseCleanedData).length,
        hasProductionLogs: !!firebaseCleanedData.productionLogs
      });
      
      // Get old project data for activity logging
      const projectRef = doc(projectCollectionRef, id);
      const oldProjectSnap = await getDoc(projectRef);
      const oldProjectData = oldProjectSnap.exists() ? oldProjectSnap.data() : {};

      // Handle saving to Firestore
      await updateDoc(projectRef, firebaseCleanedData);

      // Log activity for changed fields
      const fieldsToTrack = ['name', 'status', 'type', 'company', 'contractId'];
      for (const field of fieldsToTrack) {
        const oldValue = oldProjectData[field];
        const newValue = firebaseCleanedData[field];

        // Only log if field exists in update and the value actually changed.
        // valuesEqual covers empty-vs-empty and Timestamp-vs-Date, which a bare
        // !== reports as phantom changes.
        if (newValue !== undefined && !valuesEqual(oldValue, newValue)) {
          try {
            await projectService.logActivity(
              id,
              'field_update',
              field,
              oldValue || '',
              newValue || '',
              `Updated ${field}`
            );
            logger.debug('[AdminProjects] Logged activity for field change', {
              projectId: id,
              field,
              oldValue,
              newValue
            });
          } catch (error) {
            logger.error('[AdminProjects] Failed to log activity for field change', {
              projectId: id,
              field,
              error: error.message
            });
          }
        }
      }

      const existingProject = items.value[id] || {};
      // Update local state - merge with existing data to preserve all fields
      items.value[id] = {
        ...existingProject,  // Preserve existing data
        ...firebaseCleanedData,  // Apply updates
        id,  // Ensure ID is always present
        dateUpdated: (() => {
          try {
            const date = new Date(firebaseCleanedData.dateUpdated);
            if (isNaN(date.getTime())) {
              logger.warn('[AdminProjects] Invalid dateUpdated value, using current time', {
                projectId: id,
                dateValue: firebaseCleanedData.dateUpdated
              });
              return new Date().toLocaleString();
            }
            return date.toLocaleString();
          } catch (error) {
            logger.error('[AdminProjects] Error formatting dateUpdated:', {
              projectId: id,
              error: error.message,
              dateValue: firebaseCleanedData.dateUpdated
            });
            return new Date().toLocaleString();
          }
        })()
      };

      // Clear cache to ensure fresh data is served
      if (projectCache.has(id)) {
        projectCache.delete(id);
      }

      logger.info('[AdminProjects] Project updated successfully', { projectId: id });
      return { success: true };
    } catch (error) {
      logger.error('[AdminProjects] Failed to update project:', {
        projectId: id,
        error: error.message
      });
      throw error;
    }
  }

  /**
   * Create a new project in Firestore and add to local state
   * @param {Object} projectData - New project data
   * @param {Object} options - Creation options { isClone: boolean, sourceProjectId: string }
   */
  async function createProject(projectData, options = {}) {
    if (!projectData || !projectData.name || !projectData.companyId) {
      logger.error('[AdminProjects] Invalid project creation attempt - missing required data');
      throw new Error('Project name and company ID are required');
    }

    loading.value = true;

    try {
      logger.info('[AdminProjects] Creating new project', {
        name: projectData.name,
        companyId: projectData.companyId,
        type: projectData.type,
        isClone: options.isClone || false,
        sourceProjectId: options.sourceProjectId
      });

      // Fetch the company's default contract ID
      let defaultContractId = null;
      try {
        const companyRef = doc(db, "companies", projectData.companyId);
        const companyDoc = await getDoc(companyRef);
        
        if (companyDoc.exists()) {
          const companyData = companyDoc.data();
          defaultContractId = companyData.defaultContractId || null;
          
          if (defaultContractId) {
            logger.info('[AdminProjects] Found default contract for company', {
              companyId: projectData.companyId,
              defaultContractId
            });
          } else {
            logger.info('[AdminProjects] No default contract set for company', {
              companyId: projectData.companyId
            });
          }
        }
      } catch (error) {
        logger.warn('[AdminProjects] Failed to fetch company default contract', {
          error: error.message,
          companyId: projectData.companyId
        });
      }

      // Prepare the base project data
      const newProjectData = {
        name: projectData.name.trim(),
        company: projectData.companyId,
        companyName: projectData.companyName,
        status: projectData.status || PROJECT_STATUS.DRAFT,
        contractId: defaultContractId, // Assign the default contract ID
        createdBy: userAuthStore.user?.id || null,
        createdByName: userAuthStore.user?.name || userAuthStore.user?.email || 'Unknown',
        dateCreated: serverTimestamp(),
        dateUpdated: serverTimestamp(),
        dateSubmitted: serverTimestamp(),
        ...projectData
      };

      // Remove any non-serializable or unwanted properties
      delete newProjectData.companyId; // Use 'company' field instead
      // Default type to 'project' if not explicitly set (episodes will pass 'episode')
      if (!newProjectData.type) {
        newProjectData.type = 'project'
      }
      delete newProjectData.id; // Will be auto-generated by Firebase
      // Remove deprecated fields
      delete newProjectData.dates;
      delete newProjectData.daysCount;
      delete newProjectData.pointsTotal;
      delete newProjectData.requireTravel;
      delete newProjectData.animationTime;

      // Handle cloning logic
      if (options.isClone && options.sourceProjectId) {
        logger.info('[AdminProjects] Cloning project data', {
          sourceProjectId: options.sourceProjectId,
          targetName: projectData.name
        });

        // Get source project data (may need direct fetch for episodes filtered from store)
        let sourceProject = items.value[options.sourceProjectId];
        if (!sourceProject) {
          sourceProject = await fetchProjectById(options.sourceProjectId);
        }
        if (!sourceProject) {
          throw new Error(`Source project ${options.sourceProjectId} not found for cloning`);
        }

        // Note: Brief data is typically stored under misspelled "breif" field in existing projects
        logger.debug('[AdminProjects] Checking for brief data to clone:', {
          sourceProjectId: options.sourceProjectId,
          sourceProjectName: sourceProject.name,
          hasBriefData: !!sourceProject.briefData,
          hasBreif: !!sourceProject.breif, // misspelled field where data is usually stored
          hasBrief: !!sourceProject.brief
        });

        // Try different possible field names for brief data
        let briefDataToCopy = null;
        let fieldName = null;
        
        if (sourceProject.briefData) {
          briefDataToCopy = sourceProject.briefData;
          fieldName = 'briefData';
        } else if (sourceProject.breif) {
          // Note: checking for misspelled "breif" field (missing 'i')
          briefDataToCopy = sourceProject.breif;
          fieldName = 'breif';
        } else if (sourceProject.brief) {
          briefDataToCopy = sourceProject.brief;
          fieldName = 'brief';
        } else if (sourceProject.formData) {
          briefDataToCopy = sourceProject.formData;
          fieldName = 'formData';
        } else if (sourceProject.data) {
          briefDataToCopy = sourceProject.data;
          fieldName = 'data';
        } else if (sourceProject.briefForm) {
          briefDataToCopy = sourceProject.briefForm;
          fieldName = 'briefForm';
        } else if (sourceProject.intakeForm) {
          briefDataToCopy = sourceProject.intakeForm;
          fieldName = 'intakeForm';
        } else if (sourceProject.intake) {
          briefDataToCopy = sourceProject.intake;
          fieldName = 'intake';
        }

        if (fieldName) {
          logger.info(`[AdminProjects] Found brief data in field: ${fieldName}`);
        }

                // Clone the brief data if found
        if (briefDataToCopy) {
          // Store under the same field name as the source to maintain consistency
          if (fieldName === 'breif') {
            newProjectData.breif = JSON.parse(JSON.stringify(briefDataToCopy));
          } else if (fieldName === 'briefData') {
            newProjectData.briefData = JSON.parse(JSON.stringify(briefDataToCopy));
          } else {
            // For other field names, default to the misspelled 'breif' field for consistency
            newProjectData.breif = JSON.parse(JSON.stringify(briefDataToCopy));
          }
          
          logger.info('[AdminProjects] Brief data cloned successfully', {
            sourceProjectId: options.sourceProjectId,
            sourceField: fieldName,
            targetField: fieldName === 'briefData' ? 'briefData' : 'breif',
            briefDataKeys: typeof briefDataToCopy === 'object' ? Object.keys(briefDataToCopy).length : 'non-object',
            briefDataType: typeof briefDataToCopy,
            briefDataSize: JSON.stringify(briefDataToCopy).length
          });
        } else {
           logger.warn('[AdminProjects] No brief data found to clone - source project may not have any brief data', {
             sourceProjectId: options.sourceProjectId,
             sourceProjectName: sourceProject.name,
             checkedPrimaryFields: ['briefData', 'breif', 'brief']
           });
         }

        // Ensure the type matches the source project
        newProjectData.type = sourceProject.type;
      } else if (!options.isClone && projectData.type) {
        // Skip brief generation if the caller already provided brief data
        if (!newProjectData.breif) {
          // Handle creating new blank project with initial brief data from intake configuration
          logger.info('[AdminProjects] Creating new blank project with initial brief data', {
            projectType: projectData.type,
            targetName: projectData.name
          });

          try {
            // Ensure intake configurations are loaded
            if (!formBuilderStore.initialized) {
              await formBuilderStore.loadConfigurations();
            }

            // Generate initial brief data from the selected intake configuration
            let initialBriefData = formBuilderStore.generateFormData(projectData.type);

            if (initialBriefData && Array.isArray(initialBriefData) && initialBriefData.length > 0) {
              // Apply company MSA custom pricing to calculator sections so
              // admin-created orders snapshot the same rates clients see
              const hasCalculator = initialBriefData.some(s => s.type === 'pricing-calculator');
              if (hasCalculator && newProjectData.company) {
                try {
                  const msaPricing = await contractService.getMsaPricingForCompany(newProjectData.company);
                  if (msaPricing) {
                    const result = applyMsaOverrideToBriefSections(initialBriefData, msaPricing);
                    initialBriefData = result.sections;
                    if (result.warnings.length) {
                      logger.warn('[AdminProjects] MSA pricing override drift', { warnings: result.warnings });
                    }
                  }
                } catch (pricingError) {
                  logger.error('[AdminProjects] Failed to apply MSA custom pricing, using standard rates', {
                    error: pricingError.message,
                    companyId: newProjectData.company
                  });
                }
              }

              // Store under the "breif" field (misspelled) for consistency with existing system
              newProjectData.breif = { sections: initialBriefData };

              logger.info('[AdminProjects] Initial brief data created from intake configuration', {
                projectType: projectData.type,
                sectionCount: initialBriefData.length,
                briefDataSize: JSON.stringify(initialBriefData).length
              });
            } else {
              logger.error('[AdminProjects] No intake configuration found for project type', {
                projectType: projectData.type
              });
              throw new Error(`No intake configuration found for project type: ${projectData.type}`);
            }
          } catch (error) {
            logger.error('[AdminProjects] Failed to generate initial brief data:', {
              projectType: projectData.type,
              error: error.message
            });
            throw error; // Re-throw the error to prevent project creation
          }
        } else {
          logger.info('[AdminProjects] Brief data already provided, skipping generation', {
            projectType: projectData.type,
            targetName: projectData.name
          });
        }
      }

      // Function to recursively remove undefined values (reusing from updateProject)
      const removeUndefined = (obj) => {
        if (obj === null) {
          return null;
        }
        
        if (obj === undefined) {
          return undefined;
        }
        
        if (Array.isArray(obj)) {
          const cleanedArray = obj
            .map(item => removeUndefined(item))
            .filter(item => item !== undefined);
          return cleanedArray.length > 0 ? cleanedArray : [];
        }
        
        if (typeof obj === 'object' && obj.constructor === Object) {
          const cleaned = {};
          for (const [key, value] of Object.entries(obj)) {
            if (value !== undefined) {
              const cleanedValue = removeUndefined(value);
              if (cleanedValue !== undefined) {
                cleaned[key] = cleanedValue;
              }
            }
          }
          return Object.keys(cleaned).length > 0 ? cleaned : {};
        }
        
        if (typeof obj === 'object') {
          return obj;
        }
        
        return obj;
      };

      // Clean the data for Firebase
      const firebaseCleanedData = removeUndefined(newProjectData);

      logger.debug('[AdminProjects] Cleaned project data for creation:', {
        originalKeys: Object.keys(newProjectData).length,
        cleanedKeys: Object.keys(firebaseCleanedData).length,
        hasCompany: !!firebaseCleanedData.company,
        hasName: !!firebaseCleanedData.name,
        hasBriefData: !!firebaseCleanedData.briefData
      });

      // Create the project in Firestore
      const docRef = await addDoc(projectCollectionRef, firebaseCleanedData);

      // Prepare the local state data with the new ID
      // Note: serverTimestamp() returns a sentinel value, so we use current date for local state
      const now = new Date();
      const localProjectData = {
        id: docRef.id,
        ...firebaseCleanedData,
        dateUpdated: now.toLocaleString(),
        dateCreated: now.toLocaleString(),
        dateSubmitted: now.toLocaleString()
      };

      // Add to local state
      items.value[docRef.id] = localProjectData;

      logger.info('[AdminProjects] Project created successfully', {
        projectId: docRef.id,
        name: projectData.name,
        companyId: projectData.companyId,
        isClone: options.isClone || false
      });

      // Log project creation activity
      try {
        await projectService.logActivity(
          docRef.id,
          'created',
          null,
          null,
          projectData.name,
          `Project created: ${projectData.name}`
        );
        logger.info('[AdminProjects] Project creation activity logged', {
          projectId: docRef.id
        });
      } catch (error) {
        logger.error('[AdminProjects] Failed to log project creation activity', {
          projectId: docRef.id,
          error: error.message
        });
      }

      // Note: Notifications are now handled by onProjectCreate Firestore trigger
      // which automatically sends notifications to all admin users when a project
      // is created with "In Review" status

      // Clone deliverables if requested
      let clonedDeliverablesCount = 0;
      let failedDeliverablesCount = 0;
      if (options.isClone && options.cloneDeliverables && options.sourceProjectId) {
        try {
          logger.info('[AdminProjects] Starting deliverable cloning', {
            sourceProjectId: options.sourceProjectId,
            targetProjectId: docRef.id
          });

          // Fetch source project deliverables
          const sourceDeliverables = await deliverableService.getProjectDeliverables(options.sourceProjectId);

          if (sourceDeliverables && sourceDeliverables.length > 0) {
            logger.info('[AdminProjects] Found deliverables to clone', {
              count: sourceDeliverables.length,
              sourceProjectId: options.sourceProjectId
            });

            // Clone each deliverable
            for (const sourceDeliverable of sourceDeliverables) {
              try {
                // Prepare new deliverable data
                const newDeliverableData = {
                  title: sourceDeliverable.title,
                  type: sourceDeliverable.type,
                  description: sourceDeliverable.description || '',
                  projectId: docRef.id, // Link to new project
                  companyId: projectData.companyId,
                  companyName: projectData.companyName,
                  status: 'not_started', // Reset status
                  stage: 'not_ready', // Set stage to not_ready
                  dueDate: null, // Remove due date
                  assignedUserId: sourceDeliverable.assignedUserId || null,
                  productionEventIds: Array.isArray(sourceDeliverable.productionEventIds)
                    ? [...sourceDeliverable.productionEventIds]
                    : [],
                  // Include other relevant fields if needed
                  notes: sourceDeliverable.notes || '',
                  tags: Array.isArray(sourceDeliverable.tags) ? [...sourceDeliverable.tags] : []
                };

                // Create the cloned deliverable
                await deliverableService.createDeliverable(newDeliverableData);
                clonedDeliverablesCount++;

                logger.debug('[AdminProjects] Deliverable cloned successfully', {
                  title: sourceDeliverable.title,
                  targetProjectId: docRef.id
                });
              } catch (deliverableError) {
                failedDeliverablesCount++;
                logger.error('[AdminProjects] Failed to clone deliverable', {
                  deliverableId: sourceDeliverable.id,
                  title: sourceDeliverable.title,
                  error: deliverableError.message
                });
              }
            }

            logger.info('[AdminProjects] Deliverable cloning completed', {
              total: sourceDeliverables.length,
              successful: clonedDeliverablesCount,
              failed: failedDeliverablesCount,
              targetProjectId: docRef.id
            });
          } else {
            logger.info('[AdminProjects] No deliverables found to clone', {
              sourceProjectId: options.sourceProjectId
            });
          }
        } catch (error) {
          // Log error but don't fail the project creation
          logger.error('[AdminProjects] Deliverable cloning process failed', {
            sourceProjectId: options.sourceProjectId,
            targetProjectId: docRef.id,
            error: error.message
          });
        }
      }

      return {
        success: true,
        projectId: docRef.id,
        project: localProjectData,
        clonedDeliverables: clonedDeliverablesCount,
        failedDeliverables: failedDeliverablesCount
      };

    } catch (error) {
      logger.error('[AdminProjects] Failed to create project:', {
        name: projectData.name,
        companyId: projectData.companyId,
        isClone: options.isClone || false,
        error: error.message
      });
      throw error;
    } finally {
      loading.value = false;
    }
  }

  /**
   * Get projects for a specific company
   * @param {String} companyId - The company ID to fetch projects for
   */
  async function getCompanyProjects(companyId) {
    if (!companyId) {
      logger.warn('[AdminProjects] Company projects lookup attempted without ID');
      return;
    }

    loading.value = true;

    try {
      logger.info('[AdminProjects] Loading company projects', { companyId });
      const q = query(
        projectCollectionRef,
        where("company", "==", companyId),
        where("status", "!=", PROJECT_STATUS.ARCHIVED)
      );

      const querySnapshot = await getDocs(q);
      const projectsData = {};

      querySnapshot.forEach((doc) => {
        const docData = doc.data();
        const project = {
          id: doc.id,
          ...docData,
          dateUpdated: (() => {
            try {
              if (!docData.dateUpdated) {
                logger.warn('[AdminProjects] Missing dateUpdated for project', {
                  projectId: doc.id
                });
                return 'Unknown';
              }
              const date = new Date(docData.dateUpdated);
              if (isNaN(date.getTime())) {
                logger.warn('[AdminProjects] Invalid dateUpdated value for project', {
                  projectId: doc.id,
                  dateValue: docData.dateUpdated
                });
                return 'Invalid Date';
              }
              return date.toLocaleString();
            } catch (error) {
              logger.error('[AdminProjects] Error formatting dateUpdated for project:', {
                projectId: doc.id,
                error: error.message,
                dateValue: docData.dateUpdated
              });
              return 'Error';
            }
          })()
        };
        projectsData[project.id] = project;
      });

      // Update only the projects for this company
      items.value = {
        ...items.value,
        ...projectsData
      };

      logger.info('[AdminProjects] Company projects loaded', {
        companyId,
        count: Object.keys(projectsData).length
      });
    } catch (error) {
      logger.error('[AdminProjects] Failed to load company projects:', {
        error: error.message,
        companyId
      });
      throw error;
    } finally {
      loading.value = false;
    }
  }

  /**
   * Add a production log entry to a project
   * @param {String} projectId - Project ID to add log to
   * @param {Object} logEntry - Production log data
   */
  async function addProductionLog(projectId, logEntry) {
    if (!projectId || !logEntry) {
      logger.error('[AdminProjects] Invalid production log addition attempt - missing data');
      throw new Error('Invalid project ID or log entry provided');
    }

    try {
      const project = items.value[projectId];
      if (!project) {
        throw new Error('Project not found');
      }

      // Clean the log entry to ensure no undefined values
      const cleanLogEntry = JSON.parse(JSON.stringify(logEntry, (key, value) => {
        return value === undefined ? null : value;
      }));

      // Initialize productionLogs array if it doesn't exist
      const currentLogs = project.productionLogs || [];
      const updatedLogs = [cleanLogEntry, ...currentLogs];

      logger.debug('[AdminProjects] Adding production log:', {
        projectId,
        logEntry: cleanLogEntry,
        currentLogsCount: currentLogs.length
      });

      // Update only the productionLogs field in Firestore
      const projectRef = doc(projectCollectionRef, projectId);
      await updateDoc(projectRef, {
        productionLogs: updatedLogs,
        dateUpdated: serverTimestamp()
      });

      // Update local state
      if (items.value[projectId]) {
        items.value[projectId] = {
          ...items.value[projectId],
          productionLogs: updatedLogs,
          dateUpdated: new Date().toLocaleString()
        };
      }

      logger.info('[AdminProjects] Production log added successfully', { 
        projectId, 
        logStatus: logEntry.status 
      });
      
      return { success: true };
    } catch (error) {
      logger.error('[AdminProjects] Failed to add production log:', {
        projectId,
        error: error.message
      });
      throw error;
    }
  }

  /**
   * Bulk update the status of multiple projects
   * @param {Array<String>} projectIds - Array of project IDs to update
   * @param {String} newStatus - The new status to set
   * @returns {Object} - { success, updated, failed }
   */
  async function bulkUpdateProjectStatus(projectIds, newStatus) {
    if (!projectIds || projectIds.length === 0) {
      logger.error('[AdminProjects] Bulk update attempted without project IDs');
      throw new Error('Project IDs are required');
    }

    if (!newStatus) {
      logger.error('[AdminProjects] Bulk update attempted without new status');
      throw new Error('New status is required');
    }

    loading.value = true;
    let updatedCount = 0;
    let failedCount = 0;
    const failedProjects = [];

    try {
      logger.info('[AdminProjects] Starting bulk status update', {
        projectCount: projectIds.length,
        newStatus
      });

      // Process in batches to avoid overwhelming Firestore
      const BATCH_SIZE = 10;
      for (let i = 0; i < projectIds.length; i += BATCH_SIZE) {
        const batch = projectIds.slice(i, i + BATCH_SIZE);

        await Promise.all(batch.map(async (projectId) => {
          try {
            const project = items.value[projectId];
            if (!project) {
              logger.warn('[AdminProjects] Project not found for bulk update', { projectId });
              failedCount++;
              failedProjects.push({ id: projectId, reason: 'Project not found' });
              return;
            }

            const oldStatus = project.status;

            // Skip if status is already the same
            if (oldStatus === newStatus) {
              logger.debug('[AdminProjects] Skipping project - status unchanged', { projectId, status: newStatus });
              return;
            }

            // Update in Firestore
            const projectRef = doc(projectCollectionRef, projectId);
            await updateDoc(projectRef, {
              status: newStatus,
              dateUpdated: serverTimestamp()
            });

            // Log activity
            try {
              await projectService.logActivity(
                projectId,
                'status_change',
                'status',
                oldStatus,
                newStatus,
                `Status changed from ${oldStatus} to ${newStatus} (bulk update)`
              );
            } catch (activityError) {
              logger.warn('[AdminProjects] Failed to log activity for bulk status update', {
                projectId,
                error: activityError.message
              });
            }

            // Update local state
            items.value[projectId] = {
              ...items.value[projectId],
              status: newStatus,
              dateUpdated: new Date().toLocaleString()
            };

            // Clear from cache
            if (projectCache.has(projectId)) {
              projectCache.delete(projectId);
            }

            updatedCount++;
          } catch (error) {
            logger.error('[AdminProjects] Failed to update project in bulk operation', {
              projectId,
              error: error.message
            });
            failedCount++;
            failedProjects.push({ id: projectId, reason: error.message });
          }
        }));
      }

      logger.info('[AdminProjects] Bulk status update completed', {
        total: projectIds.length,
        updated: updatedCount,
        failed: failedCount
      });

      return {
        success: failedCount === 0,
        updated: updatedCount,
        failed: failedCount,
        failedProjects
      };
    } catch (error) {
      logger.error('[AdminProjects] Bulk status update failed:', {
        error: error.message,
        projectCount: projectIds.length
      });
      throw error;
    } finally {
      loading.value = false;
    }
  }

  /**
   * Bulk delete projects with full cascade. Runs sequentially because each
   * cascade delete is heavy (subcollections, reviews, events).
   * @param {Array<String>} projectIds - Project IDs to delete
   * @param {Function} [onProgress] - Called as (done, total) after each delete
   * @returns {{ deleted: Number, failed: Number, failedProjects: Array }}
   */
  async function bulkDeleteProjects(projectIds, onProgress) {
    if (!projectIds || projectIds.length === 0) {
      throw new Error('Project IDs are required');
    }

    let deletedCount = 0;
    let failedCount = 0;
    const failedProjects = [];
    const total = projectIds.length;

    for (const projectId of projectIds) {
      try {
        await deleteProjectWithCascade(projectId);
        deletedCount++;
      } catch (error) {
        logger.error('[AdminProjects] Failed to delete project in bulk operation', {
          projectId,
          error: error.message
        });
        failedCount++;
        failedProjects.push({ id: projectId, reason: error.message });
      }
      if (onProgress) onProgress(deletedCount + failedCount, total);
    }

    logger.info('[AdminProjects] Bulk delete completed', {
      total: projectIds.length,
      deleted: deletedCount,
      failed: failedCount
    });

    return { deleted: deletedCount, failed: failedCount, failedProjects };
  }

  /**
   * Update production logs for a project
   * @param {String} projectId - Project ID to update logs for
   * @param {Array} productionLogs - Updated production logs array
   */
  async function updateProductionLogs(projectId, productionLogs) {
    if (!projectId || !Array.isArray(productionLogs)) {
      logger.error('[AdminProjects] Invalid production logs update attempt - missing data');
      throw new Error('Invalid project ID or production logs provided');
    }

    try {
      const project = items.value[projectId];
      if (!project) {
        throw new Error('Project not found');
      }

      // Clean the production logs to ensure no undefined values
      const cleanedLogs = productionLogs.map(log => 
        JSON.parse(JSON.stringify(log, (key, value) => {
          return value === undefined ? null : value;
        }))
      );

      // Update only the productionLogs field in Firestore
      const projectRef = doc(projectCollectionRef, projectId);
      await updateDoc(projectRef, {
        productionLogs: cleanedLogs,
        dateUpdated: serverTimestamp()
      });

      // Update local state
      if (items.value[projectId]) {
        items.value[projectId] = {
          ...items.value[projectId],
          productionLogs: cleanedLogs,
          dateUpdated: new Date().toLocaleString()
        };
      }

      logger.info('[AdminProjects] Production logs updated successfully', { 
        projectId, 
        logCount: productionLogs.length 
      });
      
      return { success: true };
    } catch (error) {
      logger.error('[AdminProjects] Failed to update production logs:', {
        projectId,
        error: error.message
      });
      throw error;
    }
  }

  /**
   * Fetch deliverables for a project
   * @param {String} projectId - Project ID to fetch deliverables for
   */
  async function fetchProjectDeliverables(projectId) {
    if (!projectId) {
      logger.error('[AdminProjects] No project ID provided for fetching deliverables');
      return;
    }

    try {
      deliverablesLoading.value = true;
      
      // Unsubscribe from previous listener if exists
      if (deliverablesUnsubscribe) {
        deliverablesUnsubscribe();
      }
      
      // Subscribe to real-time updates
      deliverablesUnsubscribe = deliverableService.subscribeToProjectDeliverables(
        projectId,
        (updatedDeliverables) => {
          deliverables.value = updatedDeliverables;
          logger.info('[AdminProjects] Deliverables updated', {
            projectId,
            count: updatedDeliverables.length
          });
        }
      );
      
      // Also fetch initially
      const projectDeliverables = await deliverableService.getProjectDeliverables(projectId);
      deliverables.value = projectDeliverables;
      
      logger.info('[AdminProjects] Fetched deliverables for project', {
        projectId,
        count: projectDeliverables.length
      });
    } catch (error) {
      logger.error('[AdminProjects] Failed to fetch deliverables:', {
        projectId,
        error: error.message
      });
      deliverables.value = [];
    } finally {
      deliverablesLoading.value = false;
    }
  }

  /**
   * Add a new deliverable
   * @param {Object} deliverableData - Deliverable data
   */
  async function addDeliverable(deliverableData) {
    try {
      const result = await deliverableService.createDeliverable(deliverableData);
      
      logger.info('[AdminProjects] Deliverable created', {
        deliverableId: result.id,
        projectId: deliverableData.projectId
      });
      
      return result;
    } catch (error) {
      logger.error('[AdminProjects] Failed to add deliverable:', {
        error: error.message
      });
      throw error;
    }
  }

  /**
   * Update a deliverable
   * @param {String} deliverableId - Deliverable ID
   * @param {Object} updates - Updates to apply
   */
  async function updateDeliverable(deliverableId, updates, showToast = true) {
    try {
      const result = await deliverableService.updateDeliverable(deliverableId, updates, showToast);
      
      logger.info('[AdminProjects] Deliverable updated', {
        deliverableId,
        updates
      });
      
      return result;
    } catch (error) {
      logger[error.isValidation ? 'warn' : 'error']('[AdminProjects] Failed to update deliverable:', {
        deliverableId,
        error: error.message
      });
      throw error;
    }
  }

  /**
   * Delete a deliverable
   * @param {String} deliverableId - Deliverable ID to delete
   */
  async function deleteDeliverable(deliverableId) {
    try {
      const result = await deliverableService.deleteDeliverable(deliverableId);
      
      logger.info('[AdminProjects] Deliverable deleted', {
        deliverableId
      });
      
      return result;
    } catch (error) {
      logger.error('[AdminProjects] Failed to delete deliverable:', {
        deliverableId,
        error: error.message
      });
      throw error;
    }
  }

  /**
   * Link a production event to a deliverable
   * @param {String} deliverableId - Deliverable ID
   * @param {String} eventId - Event ID
   * @param {String} eventTitle - Event title for logging
   */
  async function linkDeliverableToEvent(deliverableId, eventId, eventTitle) {
    try {
      const result = await deliverableService.linkProductionEvent(
        deliverableId,
        eventId,
        eventTitle
      );
      
      logger.info('[AdminProjects] Linked deliverable to event', {
        deliverableId,
        eventId
      });
      
      return result;
    } catch (error) {
      logger.error('[AdminProjects] Failed to link deliverable to event:', {
        deliverableId,
        eventId,
        error: error.message
      });
      throw error;
    }
  }

  /**
   * Unlink a production event from a deliverable
   * @param {String} deliverableId - Deliverable ID
   * @param {String} eventId - Event ID
   * @param {String} eventTitle - Event title for logging
   */
  async function unlinkDeliverableFromEvent(deliverableId, eventId, eventTitle) {
    try {
      const result = await deliverableService.unlinkProductionEvent(
        deliverableId,
        eventId,
        eventTitle
      );
      
      logger.info('[AdminProjects] Unlinked deliverable from event', {
        deliverableId,
        eventId
      });
      
      return result;
    } catch (error) {
      logger.error('[AdminProjects] Failed to unlink deliverable from event:', {
        deliverableId,
        eventId,
        error: error.message
      });
      throw error;
    }
  }

  /**
   * Fetch activity logs for a deliverable
   * @param {String} deliverableId - Deliverable ID
   */
  async function fetchDeliverableActivity(deliverableId) {
    try {
      const activity = await deliverableService.getDeliverableActivity(deliverableId);
      currentDeliverableActivity.value = activity;
      
      logger.info('[AdminProjects] Fetched deliverable activity', {
        deliverableId,
        activityCount: activity.length
      });
      
      return activity;
    } catch (error) {
      logger.error('[AdminProjects] Failed to fetch deliverable activity:', {
        deliverableId,
        error: error.message
      });
      currentDeliverableActivity.value = [];
      return [];
    }
  }

  // Reset store state
  function reset() {
    items.value = {};
    loading.value = false;
    initialized.value = false;
    deliverables.value = [];
    deliverablesLoading.value = false;
    currentDeliverableActivity.value = [];
    
    // Cleanup deliverables subscription
    if (deliverablesUnsubscribe) {
      deliverablesUnsubscribe();
      deliverablesUnsubscribe = null;
    }
    
    logger.info('[AdminProjects] Store reset completed');
  }

  //-------------------------------
  // PUBLIC INTERFACE
  //-------------------------------
  const getProjectById = computed(() => {
    return (id) => {
      // Check cache first
      if (projectCache.has(id)) {
        return projectCache.get(id);
      }
      
      const project = items.value[id];
      
      // Store in cache
      if (project) {
        projectCache.set(id, project);
      }
      
      return project || {};
    };
  });

  // Get all projects with valid date information
  const getProjectsWithValidDates = computed(() => {
    return Object.values(items.value).filter(project => 
      project.dates && (
        Array.isArray(project.dates) && project.dates.length > 0 ||
        typeof project.dates === "string" ||
        typeof project.dates === "number"
      )
    );
  });

  const allProjects = computed(() => Object.values(items.value || {}));

  // Fetch a single project by ID directly from Firestore.
  // Used for episodes and other projects filtered out of the main init query.
  /**
   * Merge an already-persisted change into local state.
   * For writes that bypass the store (episodeService writes episodes directly) —
   * without this the cached project stays stale and the UI reverts the edit.
   */
  function patchLocalProject(projectId, updates) {
    if (!items.value[projectId]) return;
    items.value[projectId] = { ...items.value[projectId], ...updates };
    projectCache.delete(projectId);
  }

  async function fetchProjectById(projectId) {
    if (items.value[projectId]) return items.value[projectId]

    try {
      const docSnap = await getDoc(doc(db, 'projects', projectId))
      if (docSnap.exists()) {
        const projectData = { id: docSnap.id, ...docSnap.data() }
        items.value[projectId] = projectData
        return projectData
      }
    } catch (error) {
      logger.error('[AdminProjectsStore] Failed to fetch project by ID', { projectId, error: error.message })
    }
    return null
  }

  // Add caching for frequently accessed data
  const projectCache = new Map();

  // Clear cache when projects are updated
  const clearCache = () => {
    projectCache.clear();
  };

  return {
    // State
    items,
    loading,
    initialized,
    deliverables,
    deliverablesLoading,
    currentDeliverableActivity,
    
    // Getters
    getProjectById,
    getProjectsWithValidDates,
    allProjects,
    
    // Actions
    init,
    createProject,
    deleteProject,
    deleteProjectWithCascade,
    getProjectDeletionCounts,
    updateProject,
    bulkUpdateProjectStatus,
    bulkDeleteProjects,
    getCompanyProjects,
    addProductionLog,
    updateProductionLogs,
    
    // Deliverables Actions
    fetchProjectDeliverables,
    addDeliverable,
    updateDeliverable,
    deleteDeliverable,
    linkDeliverableToEvent,
    unlinkDeliverableFromEvent,
    fetchDeliverableActivity,
    
    // Direct fetch (bypasses episode filter)
    fetchProjectById,
    patchLocalProject,

    // Utility
    clearCache,
    reset
  };
}); 